import * as THREE from 'three';
import { FullscreenPass, makeTarget } from '../core/Fullscreen';
import { commonGLSL } from '../shaders/common';
import { atmosphereGLSL } from '../shaders/atmosphere';
import { cloudCommonGLSL, cloudMarchFrag, cloudShadowFrag, noiseGenGLSL } from '../shaders/clouds';
import { buildWeatherMap } from '../world/NoiseTextures';
import type { Atmosphere } from './Atmosphere';

export interface CloudParams {
  coverage: number;
  base: number; // m ASL
  thickness: number;
  density: number;
  type: number; // 0 stratus, 1 cumulus, 2 cumulonimbus
  darkness: number;
  erosion: number;
  anvil: number;
}

export class Clouds {
  readonly baseVol: THREE.WebGL3DRenderTarget;
  readonly detailVol: THREE.WebGL3DRenderTarget;
  rt: THREE.WebGLRenderTarget;
  readonly shadowRT = makeTarget(256, 256, { type: THREE.UnsignedByteType });
  readonly uniforms: Record<string, THREE.IUniform>;
  private readonly march: FullscreenPass;
  private readonly shadowPass: FullscreenPass;
  readonly params: CloudParams = { coverage: 0.3, base: 4300, thickness: 2200, density: 1, type: 1, darkness: 0, erosion: 0.35, anvil: 0 };
  private frame = 0;
  readonly shadowSize = 24000;
  enabled = true;

  constructor(private readonly renderer: THREE.WebGLRenderer, atmo: Atmosphere, private scale: number, steps: number) {
    const mk3d = (n: number) => {
      const t = new THREE.WebGL3DRenderTarget(n, n, n, { type: THREE.UnsignedByteType, format: THREE.RGBAFormat });
      t.texture.wrapS = t.texture.wrapT = t.texture.wrapR = THREE.RepeatWrapping;
      t.texture.minFilter = t.texture.magFilter = THREE.LinearFilter;
      t.texture.generateMipmaps = false;
      return t;
    };
    this.baseVol = mk3d(128);
    this.detailVol = mk3d(32);
    this.generateNoise();
    this.rt = makeTarget(2, 2);
    this.uniforms = {
      ...atmo.uniforms,
      uNoiseBase: { value: this.baseVol.texture },
      uNoiseDetail: { value: this.detailVol.texture },
      uWeatherMap: { value: buildWeatherMap() },
      uCloudLayer: { value: new THREE.Vector4(4300, 6500, 0.3, 1) },
      uCloudShape: { value: new THREE.Vector4(1, 0.35, 0, 0) },
      uWindOffset: { value: new THREE.Vector3() },
      uWeatherScale: { value: 42000 },
      uSceneDepth: { value: null },
      uInvProjView: { value: new THREE.Matrix4() },
      uCamPos: { value: new THREE.Vector3() },
      uSteps: { value: steps },
      uFrame: { value: 0 },
      uLightning: { value: 0 },
      uLightningPos: { value: new THREE.Vector3() },
      uCloudShadowXform: { value: new THREE.Vector4(0, 0, 1 / this.shadowSize, 1) },
    };
    const prefix = commonGLSL + atmosphereGLSL + cloudCommonGLSL;
    this.march = new FullscreenPass(cloudMarchFrag, this.uniforms, {}, prefix);
    this.shadowPass = new FullscreenPass(cloudShadowFrag, this.uniforms, {}, prefix);
  }

  private generateNoise() {
    const gen = new FullscreenPass(noiseGenGLSL, {
      uSlice: { value: 0 }, uSize: { value: 128 }, uMode: { value: 0 },
    });
    const r = this.renderer;
    for (const [vol, n, mode] of [[this.baseVol, 128, 0], [this.detailVol, 32, 1]] as const) {
      gen.material.uniforms.uSize.value = n;
      gen.material.uniforms.uMode.value = mode;
      for (let z = 0; z < n; z++) {
        gen.material.uniforms.uSlice.value = z;
        gen.render(r, vol, z);
      }
    }
    r.setRenderTarget(null);
    gen.dispose();
  }

  setQuality(scale: number, steps: number) {
    this.scale = scale;
    this.uniforms.uSteps.value = steps;
  }

  resize(w: number, h: number) {
    this.rt.dispose();
    this.rt = makeTarget(Math.max(1, Math.round(w * this.scale)), Math.max(1, Math.round(h * this.scale)));
  }

  get active() {
    return this.enabled && this.params.coverage > 0.01;
  }

  update(dt: number, wind: THREE.Vector2, camera: THREE.PerspectiveCamera, depth: THREE.Texture, lightning: number, lightningPos: THREE.Vector3) {
    const u = this.uniforms;
    const p = this.params;
    u.uCloudLayer.value.set(p.base, p.base + p.thickness, p.coverage, p.density);
    u.uCloudShape.value.set(p.type, p.erosion, p.darkness, p.anvil);
    u.uWindOffset.value.x += wind.x * dt;
    u.uWindOffset.value.z += wind.y * dt;
    u.uSceneDepth.value = depth;
    u.uInvProjView.value.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).invert();
    u.uCamPos.value.copy(camera.position);
    u.uFrame.value = (this.frame++ % 64);
    u.uLightning.value = lightning;
    u.uLightningPos.value.copy(lightningPos);
    // cloud shadow map follows the camera (snapped to texels to avoid swimming)
    const texel = this.shadowSize / 256;
    const x0 = Math.floor((camera.position.x - this.shadowSize / 2) / texel) * texel;
    const z0 = Math.floor((camera.position.z - this.shadowSize / 2) / texel) * texel;
    u.uCloudShadowXform.value.set(x0, z0, 1 / this.shadowSize, this.active ? 1 : 0);
    if (!this.active) return;
    this.shadowPass.render(this.renderer, this.shadowRT);
    this.renderer.setRenderTarget(null);
  }

  /** Ray-march the cloud layer (after the scene depth is available). */
  render() {
    if (!this.active) return;
    this.march.render(this.renderer, this.rt);
    this.renderer.setRenderTarget(null);
  }
}
